import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PayeeVerificationResult } from '@shared/contracts/statuses';
import { createBeneficiary, getBeneficiaries, verifyPayee, type NewBeneficiaryInput } from '@/mocks/api';
import { useAsync, useMutation } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { CAPABILITIES, resolveCapability } from '@/services/entitlements';
import { newIdempotencyKey } from '@/services/idempotency';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Initials, ListRow, StatusBadge } from '@/components/banking';
import { EmptyState, ErrorState, LoadingRegion, Notice, RowSkeleton } from '@/components/feedback';
import { Sheet } from '@/components/feedback/Sheet';
import { useToast } from '@/components/feedback/Toast';
import { formatIban, formatSortCode } from '@/services/formatters';
import { isMockApiError } from '@/mocks/errors';

/**
 * Beneficiaries — WF-SMEM-05.
 *
 * Creating one runs payee verification (Confirmation of Payee) before the
 * beneficiary is saved, and the result travels with the record so every later
 * payment can show it. A non-UK destination reports `unavailable` honestly
 * rather than claiming a match the scheme cannot provide.
 */
export function BeneficiariesPage() {
  const resolved = resolveContext();
  const toast = useToast();
  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    country: 'GB',
    currency: 'GBP',
    accountNumber: '',
    sortCode: '',
    iban: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [verification, setVerification] = useState<{
    result: PayeeVerificationResult;
    matchedName?: string;
  } | null>(null);
  const idempotencyKey = useRef<string>(newIdempotencyKey('ben'));

  const list = useAsync(async () => {
    if (!companyId) return [];
    return (await getBeneficiaries(companyId, search || undefined)).data;
  }, [companyId, search]);

  const verifyMutation = useMutation(async (input: NewBeneficiaryInput) =>
    (await verifyPayee(input)).data,
  );

  const createMutation = useMutation(
    async (input: NewBeneficiaryInput, result: PayeeVerificationResult) => {
      if (!companyId || !userId) throw new Error('no session');
      return (await createBeneficiary(companyId, userId, input, result, idempotencyKey.current))
        .data;
    },
  );

  if (!resolved) return null;

  const canCreate = resolveCapability(resolved.entitlements, CAPABILITIES.beneficiariesCreate).allowed;

  const asInput = (): NewBeneficiaryInput => ({
    name: form.name,
    type: 'business',
    country: form.country,
    currency: form.currency,
    ...(form.accountNumber ? { accountNumber: form.accountNumber } : {}),
    ...(form.sortCode ? { sortCode: form.sortCode } : {}),
    ...(form.iban ? { iban: form.iban } : {}),
  });

  const runVerify = async () => {
    setFieldErrors({});
    if (!form.name.trim()) {
      setFieldErrors({ name: 'Enter the beneficiary name.' });
      return;
    }
    const outcome = await verifyMutation.run(asInput());
    if (outcome.ok) setVerification(outcome.value);
  };

  const save = async () => {
    const outcome = await createMutation.run(asInput(), verification?.result ?? 'unavailable');
    if (outcome.ok) {
      setAddOpen(false);
      setVerification(null);
      setForm({ name: '', country: 'GB', currency: 'GBP', accountNumber: '', sortCode: '', iban: '' });
      idempotencyKey.current = newIdempotencyKey('ben');
      list.reload();
      toast.show({ message: `${outcome.value.name} added.` });
    } else if (isMockApiError(outcome.error) && outcome.error.fieldErrors) {
      setFieldErrors(outcome.error.fieldErrors);
    }
  };

  const rows = list.data ?? [];

  return (
    <>
      <MobileHeader
        title="Beneficiaries"
        backTo="/mobile/more"
        backLabel="More"
        trailing={
          canCreate ? (
            <button
              type="button"
              className="tm-btn tm-btn--quiet tm-btn--sm"
              onClick={() => {
                setVerification(null);
                setFieldErrors({});
                setAddOpen(true);
              }}
            >
              Add
            </button>
          ) : null
        }
      />

      <div className="tm-stack">
        <div className="tm-field">
          <label className="tm-label tm-visually-hidden" htmlFor="ben-search">
            Search beneficiaries
          </label>
          <input
            id="ben-search"
            className="tm-input"
            type="search"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {list.status === 'loading' ? (
          <LoadingRegion label="Loading beneficiaries">
            <div className="tm-card tm-card--flush">
              <RowSkeleton count={4} />
            </div>
          </LoadingRegion>
        ) : list.status === 'error' ? (
          <ErrorState error={list.error} onRetry={list.reload} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={search ? 'No matching beneficiaries' : 'No beneficiaries yet'}
            body={
              search
                ? 'Try a different name.'
                : 'Add the people and businesses you pay so you can send money quickly.'
            }
          />
        ) : (
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              {rows.map((b) => (
                <li key={b.id}>
                  <ListRow
                    to={`/mobile/payments/new?beneficiaryId=${b.id}`}
                    leading={<Initials name={b.name} />}
                    title={b.name}
                    meta={
                      b.iban
                        ? formatIban(b.iban)
                        : `${formatSortCode(b.sortCode ?? '')} · ${b.accountNumber ?? ''}`
                    }
                    end={
                      <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                        {b.status === 'disabled' ? (
                          <StatusBadge status="disabled" label="Disabled" tone="neutral" />
                        ) : b.verificationResult !== 'match' ? (
                          <StatusBadge status={b.verificationResult} />
                        ) : null}
                      </span>
                    }
                    ariaLabel={`${b.name}, payee check ${b.verificationResult}${b.status === 'disabled' ? ', disabled' : ''}`}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="tm-hint">
          A disabled beneficiary stays visible for your records but cannot be paid.
        </p>

        <Link
          to="/mobile/continue-on-web?task=team-permissions"
          className="tm-btn tm-btn--secondary tm-btn--block"
        >
          Manage beneficiaries on web
        </Link>
      </div>

      {/* Add beneficiary: verify, then save. */}
      <Sheet open={addOpen} onClose={() => setAddOpen(false)} title="Add a beneficiary">
        <div className="tm-stack">
          <div className="tm-field">
            <label className="tm-label" htmlFor="ben-name">
              Name on the account
            </label>
            <input
              id="ben-name"
              className="tm-input"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value });
                setVerification(null);
              }}
              aria-invalid={fieldErrors['name'] ? true : undefined}
              aria-describedby={fieldErrors['name'] ? 'ben-name-error' : undefined}
            />
            <div className="tm-field__error-slot">
              {fieldErrors['name'] ? (
                <span className="tm-field__error" id="ben-name-error">
                  {fieldErrors['name']}
                </span>
              ) : null}
            </div>
          </div>

          <div className="tm-field">
            <label className="tm-label" htmlFor="ben-country">
              Country
            </label>
            <select
              id="ben-country"
              className="tm-select"
              value={form.country}
              onChange={(e) => {
                const country = e.target.value;
                setForm({ ...form, country, currency: country === 'GB' ? 'GBP' : 'EUR' });
                setVerification(null);
              }}
            >
              <option value="GB">United Kingdom</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
            </select>
            <div className="tm-field__error-slot" />
          </div>

          {form.country === 'GB' ? (
            <>
              <div className="tm-field">
                <label className="tm-label" htmlFor="ben-sort">
                  Sort code
                </label>
                <input
                  id="ben-sort"
                  className="tm-input"
                  inputMode="numeric"
                  maxLength={8}
                  value={form.sortCode}
                  onChange={(e) => {
                    setForm({ ...form, sortCode: e.target.value });
                    setVerification(null);
                  }}
                  aria-invalid={fieldErrors['sortCode'] ? true : undefined}
                  aria-describedby={fieldErrors['sortCode'] ? 'ben-sort-error' : undefined}
                />
                <div className="tm-field__error-slot">
                  {fieldErrors['sortCode'] ? (
                    <span className="tm-field__error" id="ben-sort-error">
                      {fieldErrors['sortCode']}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="tm-field">
                <label className="tm-label" htmlFor="ben-acct">
                  Account number
                </label>
                <input
                  id="ben-acct"
                  className="tm-input"
                  inputMode="numeric"
                  maxLength={8}
                  value={form.accountNumber}
                  onChange={(e) => {
                    setForm({ ...form, accountNumber: e.target.value });
                    setVerification(null);
                  }}
                  aria-invalid={fieldErrors['accountNumber'] ? true : undefined}
                  aria-describedby={fieldErrors['accountNumber'] ? 'ben-acct-error' : undefined}
                />
                <div className="tm-field__error-slot">
                  {fieldErrors['accountNumber'] ? (
                    <span className="tm-field__error" id="ben-acct-error">
                      {fieldErrors['accountNumber']}
                    </span>
                  ) : null}
                </div>
              </div>
            </>
          ) : (
            <div className="tm-field">
              <label className="tm-label" htmlFor="ben-iban">
                IBAN
              </label>
              <input
                id="ben-iban"
                className="tm-input"
                value={form.iban}
                onChange={(e) => {
                  setForm({ ...form, iban: e.target.value.toUpperCase() });
                  setVerification(null);
                }}
                aria-invalid={fieldErrors['iban'] ? true : undefined}
                aria-describedby={fieldErrors['iban'] ? 'ben-iban-error' : undefined}
              />
              <div className="tm-field__error-slot">
                {fieldErrors['iban'] ? (
                  <span className="tm-field__error" id="ben-iban-error">
                    {fieldErrors['iban']}
                  </span>
                ) : null}
              </div>
            </div>
          )}

          {verification ? (
            <Notice
              tone={
                verification.result === 'match'
                  ? 'success'
                  : verification.result === 'no-match'
                    ? 'danger'
                    : 'warning'
              }
              title={
                verification.result === 'match'
                  ? 'Name matches'
                  : verification.result === 'close-match'
                    ? 'Close match only'
                    : verification.result === 'no-match'
                      ? 'Name does not match'
                      : 'Name check unavailable'
              }
            >
              {verification.result === 'match'
                ? 'The receiving bank confirmed this name.'
                : verification.result === 'close-match'
                  ? `The bank holds "${verification.matchedName ?? 'a similar name'}". Check before you save.`
                  : verification.result === 'no-match'
                    ? 'The bank holds a different name for this account.'
                    : 'Account name checking is not available for this country, so we cannot confirm the name.'}
            </Notice>
          ) : null}

          {verification === null ? (
            <button
              type="button"
              className="tm-btn tm-btn--block"
              disabled={verifyMutation.pending}
              onClick={() => void runVerify()}
            >
              {verifyMutation.pending ? 'Checking the name…' : 'Check the name'}
            </button>
          ) : (
            <button
              type="button"
              className="tm-btn tm-btn--block"
              disabled={createMutation.pending}
              onClick={() => void save()}
            >
              {createMutation.pending ? 'Saving…' : 'Save beneficiary'}
            </button>
          )}
        </div>
      </Sheet>
    </>
  );
}
